import { useEffect, useState } from "react";
import axios from "axios";
import { X } from "lucide-react";
import { getImageUrl } from "@/utils/imageUrl";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Gallery = () => {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(null);

  useEffect(() => {
    const fetchAllImages = async () => {
      try {
        // Fetch gallery, cars and settings (for services)
        const [galleryRes, carsRes, settingsRes] = await Promise.all([
          axios.get(`${API}/gallery`),
          axios.get(`${API}/cars`),
          axios.get(`${API}/settings`)
        ]);
        
        // Gallery images
        const galleryImages = galleryRes.data.map(img => ({
          id: img.id,
          url: img.url,
          titre: img.titre || "Galerie",
          description: img.description,
          category: "galerie"
        }));
        
        // Car images (first image of each car)
        const carImages = carsRes.data
          .filter(car => car.images && car.images.length > 0)
          .map(car => ({
            id: `car-${car.id}`,
            url: car.images[0],
            titre: `${car.marque} ${car.modele}`,
            description: `${car.annee} - ${car.kilometrage?.toLocaleString()} km`,
            category: "voitures"
          }));
        
        // Service images
        const settings = settingsRes.data;
        const serviceImages = [];
        
        // Parse service images from settings
        for (let i = 1; i <= 6; i++) {
          const serviceTitle = settings[`service${i}_titre`];
          const serviceImagesStr = settings[`service${i}_images`];
          
          if (serviceImagesStr) {
            const imgs = serviceImagesStr.split(',').filter(s => s.trim());
            imgs.forEach((imgUrl, idx) => {
              serviceImages.push({
                id: `service-${i}-${idx}`,
                url: imgUrl.trim(),
                titre: serviceTitle || `Service ${i}`,
                description: "Nos réalisations",
                category: "services"
              });
            });
          }
        }
        
        // Combine all images
        setImages([...carImages, ...serviceImages, ...galleryImages]);
      } catch (error) {
        console.error("Error fetching images:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchAllImages();
  }, []);

  return (
    <div data-testid="gallery-page">
      {/* Hero */}
      <section className="py-20 bg-gray-50">
        <div className="page-container">
          <h1 className="text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
            NOTRE <span className="text-yellow-500">GALERIE</span>
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl">
            Découvrez nos véhicules, nos services et nos réalisations.
          </p>
        </div>
      </section>

      {/* Gallery Grid */}
      <section className="section-spacing bg-white">
        <div className="page-container">
          {loading ? (
            <div className="gallery-grid">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="aspect-video bg-gray-200 rounded-xl animate-pulse" />
              ))}
            </div>
          ) : images.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-gray-600">Aucune image dans la galerie pour le moment.</p>
            </div>
          ) : (
            <div className="gallery-grid" data-testid="gallery-grid">
              {images.map((image, index) => (
                <div 
                  key={image.id}
                  className="gallery-item group animate-fade-in-up"
                  style={{ animationDelay: `${index * 50}ms` }}
                  onClick={() => setSelectedImage(image)}
                >
                  <img
                    src={getImageUrl(image.url)}
                    alt={image.titre}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-6">
                    <div>
                      <span className="text-yellow-400 text-xs font-medium uppercase tracking-wider">
                        {image.category === "voitures" ? "Véhicule" : image.category === "services" ? "Service" : "Galerie"}
                      </span>
                      <h3 className="text-white font-bold text-lg">{image.titre}</h3>
                      {image.description && (
                        <p className="text-gray-200 text-sm mt-1">{image.description}</p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Lightbox */}
      {selectedImage && (
        <div 
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
          onClick={() => setSelectedImage(null)}
          data-testid="lightbox"
        >
          <button 
            className="absolute top-6 right-6 text-white hover:text-yellow-500 transition-colors"
            onClick={() => setSelectedImage(null)}
          >
            <X className="w-8 h-8" />
          </button>
          <div className="max-w-5xl w-full" onClick={(e) => e.stopPropagation()}>
            <img
              src={getImageUrl(selectedImage.url)}
              alt={selectedImage.titre}
              className="w-full h-auto rounded-xl max-h-[80vh] object-contain"
            />
            <div className="mt-4 text-center">
              <span className="text-yellow-400 text-xs font-medium uppercase tracking-wider">
                {selectedImage.category === "voitures" ? "Véhicule" : selectedImage.category === "services" ? "Service" : "Galerie"}
              </span>
              <h3 className="text-white font-bold text-xl">{selectedImage.titre}</h3>
              {selectedImage.description && (
                <p className="text-gray-300 mt-2">{selectedImage.description}</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Gallery;
